import React, { useContext } from "react";
import { func, shape, number } from "prop-types";
import classnames from "classnames";
import { Link as ReactRouterLink } from "react-router-dom";
import { withRouter } from "react-router-dom";
import { useOffline } from "../../../contexts/OfflineContext";

import NotificationContext from "../../../contexts/NotificationsContext";
import AppDivisionContext from "../../../contexts/AppDivisionContext";
import Popper from "../Popper/Popper";
import inspectionApi from "../../../utils/api/inspection";

// Notification links are backend-generated (notification_types.link_template)
// and come in three shapes: division-scoped (`/divisions/3/projects/7442`),
// inspection links carrying the division in the query string
// (`/inspection/398444/questionnaire?divisionId=3`), and links with no division
// at all (`/projects/3288/inspections`, `/dashboard`).
const DIVISION_PATH = /^\/divisions\/(\d+)(?:\/|$)/;
const INSPECTION_PATH = /^\/inspection\/(\d+)(?:\/|$)/;

function NotificationPopper(props) {
  const notificationContext = useContext(NotificationContext);
  const { getPath } = useContext(AppDivisionContext);
  const { saveAppDivisionId } = useContext(AppDivisionContext)
  const  isOffline = useOffline();

function handleNotificationClick(id, location) {

    if( isOffline ) {
      props.handleClose();
      
return;
    }

    notificationContext.markNotificationAsRead(id);
    props.handleClose(id);
    if (location.match(/^https:\/\/\S*/)) {
      window.location = location;
    } else {
      props.history.push(getPath(location));
      syncAppDivision(location);
    }
  }

  /**
   * Point the app at the division that owns the notification target, so the
   * route we just pushed resolves against the right division.
   *
   * Note the division id must come from `location` (the raw link), never from
   * `getPath(location)` — `getPath` prefixes division-scoped routes with the
   * *current* division, which is precisely the value we are trying to replace.
   */
  function syncAppDivision(location) {
    const [pathname, search] = location.split('?');

    const queryDivisionId = Number(new URLSearchParams(search).get('divisionId'));

    if (queryDivisionId) {
      saveAppDivisionId(queryDivisionId);

      return;
    }

    const divisionMatch = pathname.match(DIVISION_PATH);

    if (divisionMatch) {
      saveAppDivisionId(Number(divisionMatch[1]));

      return;
    }

    const inspectionMatch = pathname.match(INSPECTION_PATH);

    if (inspectionMatch) {
      inspectionApi
        .getInspectionLinkedAllData(inspectionMatch[1])
        .then((res) => {
          // getInspectionLinkedAllData swallows errors and resolves undefined,
          // and an inspection is not guaranteed to carry a linked project.
          const divisionId = res?.project?.divisionProject?.division_id;

          if (divisionId) {
            saveAppDivisionId(divisionId);
          }
        });
    }
  }

  const Link = ({ children, ...rest }) => {
    // Create a handler that prevents navigation when offline
    const handleClick = (e) => {
      if (isOffline) {
        e.preventDefault();
        
return;
      }
      
      if (props.handleClose) {
        props.handleClose(e);
      }
    };

    return (
      <ReactRouterLink 
        {...rest} 
        onClick={handleClick}
      >
        {children}
      </ReactRouterLink>
    );
  };

  return (
    <Popper
      classes="notification-menu"
      coordinates={props.coordinates}
      handleClose={props.handleClose}
    >
      <div className="header">
        <h3>Notifications</h3>
        <Link className="no-underline" to="/my-profile" style={isOffline ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
          View Settings
        </Link>
      </div>

      <ul className="nav-list">
        {notificationContext.notifications.map(
          ({ id, title, content, date, link, is_opened }) => (
            <li
              key={id}
              className={classnames("nav-list-item pointer", {
                isOpened: is_opened,
              })}
              onClick={() => handleNotificationClick(id, link)}
              style={isOffline ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            >
              <span className="title">{title}</span>
              <div className="notification-detail">
                <span>{content}</span>
                <span>{date}</span>
              </div>
            </li>
          ),
        )}
      </ul>
    </Popper>
  );
}

NotificationPopper.propTypes = {
  handleClose: func.isRequired,
  coordinates: shape({
    top: number,
    bottom: number,
    left: number,
    right: number,
  }).isRequired,
};

export default withRouter(NotificationPopper);
